
import { Cpu, Clock, Layers, Sliders, ChevronDown, ChevronRight, FileText } from "lucide-react";
import type { SimulationResponse } from "../../gen/models";
import type { WordDiffToken } from "./simulatorDiffUtils";

interface SimulationResultViewProps {
  simResult: SimulationResponse;
  oldTokens: WordDiffToken[];
  newTokens: WordDiffToken[];
  tokenDiff: number;
  latencyDiff: number;
  injectedCount: number;
  excludedCount: number;
  toolsCalled: string[];
  rulesApplied: string[];
  knowledgeUsed: string[];
  showAuditing: boolean;
  onToggleAuditing: () => void;
}

export function SimulationResultView({
  simResult,
  oldTokens,
  newTokens,
  tokenDiff,
  latencyDiff,
  injectedCount,
  excludedCount,
  toolsCalled,
  rulesApplied,
  knowledgeUsed,
  showAuditing,
  onToggleAuditing,
}: SimulationResultViewProps) {
  return (
    <div className="space-y-4">
      {/* Telemetry Delta Strip */}
      <div className="p-3.5 rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-2xs flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-4">
          {/* Tokens Delta */}
          <div className="flex items-center gap-1.5">
            <Cpu className="w-4 h-4 text-stone-400" />
            <span className="text-stone-500">Tokens:</span>
            <span className="font-bold text-stone-800 dark:text-stone-100">
              {simResult.simulated_output.tokens}
            </span>
            {simResult.delta_metrics && (
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                  tokenDiff <= 0
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
                    : "bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300"
                }`}
              >
                {tokenDiff > 0 ? "+" : ""}
                {tokenDiff}
              </span>
            )}
          </div>

          {/* Latency Delta */}
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-stone-400" />
            <span className="text-stone-500">Latency:</span>
            <span className="font-bold text-stone-800 dark:text-stone-100">
              {simResult.simulated_output.latency_ms}ms
            </span>
            {simResult.delta_metrics && (
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                  latencyDiff <= 0
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
                    : "bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"
                }`}
              >
                {latencyDiff > 0 ? "+" : ""}
                {latencyDiff}ms
              </span>
            )}
          </div>

          {/* Rules Applied */}
          <div className="flex items-center gap-1.5">
            <Layers className="w-4 h-4 text-indigo-500" />
            <span className="text-stone-500">Rules Active:</span>
            <span className="font-bold text-indigo-600 dark:text-indigo-400">
              {simResult.rule_evaluation.active_rules_count}
            </span>
            <span className="text-[10px] text-stone-400">
              ({injectedCount} injected, {excludedCount} excluded)
            </span>
          </div>

          {/* Model Used & Cost */}
          {simResult.simulated_output.model_name && (
            <div className="flex items-center gap-1.5 font-mono text-[11px]">
              <span className="px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
                {simResult.simulated_output.model_name}
              </span>
              {simResult.simulated_output.estimated_cost_usd !== undefined &&
                simResult.simulated_output.estimated_cost_usd !== null && (
                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                    ~${Number(simResult.simulated_output.estimated_cost_usd).toFixed(4)}
                  </span>
                )}
            </div>
          )}
        </div>

        {/* Tool Calls */}
        <div className="flex items-center gap-1.5">
          <span className="text-stone-400 text-[11px]">Tools called:</span>
          {toolsCalled.length > 0 ? (
            toolsCalled.map((tool, idx) => (
              <span
                key={idx}
                className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-sky-50 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-800"
              >
                {tool}
              </span>
            ))
          ) : (
            <span className="text-stone-400 text-[10px] italic">None</span>
          )}
        </div>
      </div>

      {/* Word-Level Diff Split-Screen Verification Panel */}
      <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 overflow-hidden shadow-2xs">
        <div className="grid grid-cols-2 border-b border-stone-200 dark:border-stone-800 bg-stone-50/75 dark:bg-stone-800/50 text-xs font-semibold text-stone-700 dark:text-stone-300">
          <div className="px-4 py-2 border-r border-stone-200 dark:border-stone-800 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-stone-400" />
              <span>Historical Baseline Output</span>
            </div>
            <span className="text-[10px] text-stone-400 font-normal">
              {simResult.baseline_output?.latency_ms || 0}ms • {simResult.baseline_output?.tokens || 0} tok
            </span>
          </div>
          <div className="px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-emerald-700 dark:text-emerald-300">Simulated Candidate Output</span>
            </div>
            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-normal">
              {simResult.simulated_output.latency_ms}ms • {simResult.simulated_output.tokens} tok
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 divide-x divide-stone-200 dark:divide-stone-800 text-xs min-h-[160px] max-h-96 overflow-y-scroll [scrollbar-gutter:stable]">
          {/* Left: Baseline Words */}
          <div className="p-4 bg-stone-50/30 dark:bg-stone-950/40 leading-relaxed font-sans select-text">
            {oldTokens.length > 0 ? (
              oldTokens.map((t, idx) => (
                <span
                  key={idx}
                  className={
                    t.type === "removed"
                      ? "bg-rose-100 dark:bg-rose-950/70 text-rose-800 dark:text-rose-300 line-through decoration-rose-400 px-0.5 rounded"
                      : "text-stone-700 dark:text-stone-300"
                  }
                >
                  {t.value}
                </span>
              ))
            ) : (
              <span className="text-stone-400 italic">No historical baseline available.</span>
            )}
          </div>

          {/* Right: Simulated Words */}
          <div className="p-4 bg-white dark:bg-stone-900 leading-relaxed font-sans select-text">
            {newTokens.map((t, idx) => (
              <span
                key={idx}
                className={
                  t.type === "added"
                    ? "bg-emerald-100 dark:bg-emerald-950/70 text-emerald-900 dark:text-emerald-200 font-medium px-0.5 rounded"
                    : "text-stone-700 dark:text-stone-300"
                }
              >
                {t.value}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Collapsible Rules & Knowledge Auditing Panel */}
      <div className="rounded-xl border border-stone-200 dark:border-stone-800 overflow-hidden bg-white dark:bg-stone-900">
        <button
          type="button"
          onClick={onToggleAuditing}
          className="w-full px-4 py-2.5 flex items-center justify-between text-xs font-semibold text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800/40 transition"
        >
          <div className="flex items-center gap-2">
            <Sliders className="w-3.5 h-3.5 text-stone-400" />
            <span>Audit Rules &amp; Knowledge Applied</span>
            <span className="px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-800 text-[10px] text-stone-500 font-normal">
              {rulesApplied.length} rules, {knowledgeUsed.length} sources
            </span>
          </div>
          {showAuditing ? (
            <ChevronDown className="w-4 h-4 text-stone-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-stone-400" />
          )}
        </button>

        {showAuditing && (
          <div className="p-4 border-t border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 space-y-4 text-xs">
            <div className="grid grid-cols-2 gap-4">
              {/* Rules Applied */}
              <div className="space-y-1.5">
                <h4 className="font-semibold text-stone-900 dark:text-stone-100 flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-indigo-500" />
                  <span>Candidate Rules Applied:</span>
                </h4>
                <ul className="space-y-1 pl-1">
                  {rulesApplied.map((rule, idx) => (
                    <li key={idx} className="flex items-center gap-1.5 text-stone-700 dark:text-stone-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Knowledge Consulted */}
              <div className="space-y-1.5">
                <h4 className="font-semibold text-stone-900 dark:text-stone-100 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Knowledge Items Consulted:</span>
                </h4>
                <ul className="space-y-1 pl-1">
                  {knowledgeUsed.length > 0 ? (
                    knowledgeUsed.map((src, idx) => (
                      <li key={idx} className="flex items-center gap-1.5 text-stone-700 dark:text-stone-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span>{src}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-stone-400 italic">None consulted</li>
                  )}
                </ul>
              </div>
            </div>

            {/* Execution Trace Events Timeline */}
            {simResult.trace_events && simResult.trace_events.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-stone-200 dark:border-stone-800">
                <h4 className="font-semibold text-stone-900 dark:text-stone-100">
                  Harness Execution Trace:
                </h4>
                <div className="flex flex-wrap items-center gap-2">
                  {simResult.trace_events.map((evt, idx) => (
                    <div
                      key={idx}
                      className="px-2.5 py-1 rounded-md bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 text-[11px] flex items-center gap-1.5 shadow-2xs font-mono"
                    >
                      <span className="font-bold text-stone-400">#{evt.step}</span>
                      <span className="text-stone-800 dark:text-stone-200 font-semibold">{evt.type}</span>
                      {evt.name && <span className="text-stone-500">({evt.name})</span>}
                      <span className="text-stone-400 text-[10px]">{evt.duration_ms}ms</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
